/* eslint-disable */
import saveStoreDraft from "../../../utils/saveStoreDraft";
import useStoreFormDraft from "../../../hooks/useStoreFormDraft";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import { applyStoreDraft } from "../../../redux/store/store";
import { doc } from "firebase/firestore";
import { db } from "../../../firebase";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import {
  Check,
  RefreshCw,
  Info,
  FileSpreadsheet,
  ShieldAlert,
  Database,
  Settings,
  X,
} from "lucide-react";
import apicache from "../../../components/clearCach/clear";
import { useTranslation } from "react-i18next";

export default function ActivationGooglesheet({ close }) {
  const { t } = useTranslation();
  const store = useSelector((state) => state.StoreReducer);
  const dispatch = useDispatch();
  const MySwal = withReactContent(Swal);
  const [loading, setLoading] = useState(false);
  const products = useSelector((state) => state.ProductsReducer);
  const account = useSelector((state) => state.AccountReducer);

  const showAlert = (icon, title, text) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: t("servicePageActivation.googleSheet.alerts.confirm"),
      confirmButtonColor: "rgb(141, 93, 167)",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      backdrop: `
        rgba(0,0,0,0.7)
      `,
      position: "center",
      customClass: {
        popup: "swal-popup-on-top",
        backdrop: "swal-backdrop-on-top",
        container: "swal-container-on-top",
      },
      willOpen: () => {
        // Ensure this alert appears on top of everything
        document.querySelector(".swal2-container").style.zIndex = "9999999999";
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if the URL is being cleared (service deactivation)
    const isDeactivating = !data.googleShit || data.googleShit.trim() === "";

    // If not deactivating, validate the form inputs
    if (!isDeactivating) {
      // Validate URL format
      if (
        !(
          data.googleShit.startsWith("https://script.google.com/macros/") &&
          data.googleShit.endsWith("/exec")
        )
      ) {
        await showAlert(
          "warning",
          t("servicePageActivation.googleSheet.alerts.invalidUrl.title"),
          t("servicePageActivation.googleSheet.alerts.invalidUrl.text")
        );
        return;
      }

      // Check if a language is selected
      if (!data.frenchs && !data.arabics && !data.numberS) {
        await showAlert(
          "warning",
          t("servicePageActivation.googleSheet.alerts.noLanguageSelected.title"),
          t("servicePageActivation.googleSheet.alerts.noLanguageSelected.text")
        );
        return;
      }
    }

    apicache(account, products);
    setLoading(true);
    try {
      // If deactivating, clear all related service settings
      if (isDeactivating) {
        const updatedData = {
          ...data,
          googleShit: "",
          frenchs: false,
          arabics: false,
          numberS: false,
        };

        await saveStoreDraft(doc(db, "store", data.id), store, updatedData);
        dispatch(applyStoreDraft(store, updatedData));
        setData(updatedData);
        setLoading(false);
        await showAlert("success", t("servicePageActivation.googleSheet.alerts.successDeactivate.title"), "");
      } else {
        // Normal activation flow
        await saveStoreDraft(doc(db, "store", data.id), store, data);
        dispatch(applyStoreDraft(store, data));
        setLoading(false);
        await showAlert("success", t("servicePageActivation.googleSheet.alerts.successActivate.title"), "");
        close();
      }
    } catch (error) {
      setLoading(false);
      await showAlert(
        "error",
        isDeactivating
          ? t("servicePageActivation.googleSheet.alerts.errorDeactivate.title")
          : t("servicePageActivation.googleSheet.alerts.errorActivate.title"),
        t("servicePageActivation.googleSheet.alerts.errorDeactivate.text")
      );
      console.error("Error saving Google Sheet settings:", error);
    }
  };

  const [data, setData] = useStoreFormDraft(store);

  const handleFrench = () => {
    setData({ ...data, frenchs: true, arabics: false, numberS: false });
  };

  const handleArabic = () => {
    setData({ ...data, frenchs: false, arabics: true, numberS: false });
  };

  const handleNumber = () => {
    setData({ ...data, frenchs: false, arabics: false, numberS: true });
  };

  const clearGoogleSheet = () => {
    setData({ ...data, googleShit: "" });
  };

  // Add this style to ensure SweetAlert appears on top
  useEffect(() => {
    // Add CSS to make sure SweetAlert appears on top of other modals
    const style = document.createElement("style");
    style.innerHTML = `
      .swal-container-on-top {
        z-index: 9999999999 !important;
      }
      .swal-backdrop-on-top {
        background: rgba(0,0,0,0.7) !important;
      }
      .swal-popup-on-top {
        position: relative;
        box-shadow: 0 0 20px rgba(0,0,0,0.2);
      }
      body.swal2-shown {
        overflow: hidden;
      }
      body.swal2-shown > [aria-hidden="true"] {
        filter: blur(3px);
      }
    `;
    document.head.appendChild(style);

    // Add a click handler to ensure modal shows on top
    const handleDocumentClick = () => {
      const swalContainer = document.querySelector(".swal2-container");
      if (swalContainer) {
        swalContainer.style.zIndex = "9999999999";
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.head.removeChild(style);
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  // Check if service is currently active
  const isServiceActive = data.googleShit && data.googleShit.trim() !== "";

  return (
    <div className="min-h-[400px] h-fit p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#9e68ba] mb-2">
          {t("servicePageActivation.googleSheet.headerTitle")}
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          {t("servicePageActivation.googleSheet.headerDescription")}
        </p>
      </div>

      {/* Service Status Indicator */}
      <div
        className={`mb-4 p-3 rounded-lg ${isServiceActive
            ? "bg-green-50 dark:!bg-green-900/30 border border-green-200 dark:!border-green-700"
            : "bg-gray-50 dark:!bg-[#222222] border border-gray-200 dark:!border-[#3c3a3d]"
          }`}
      >
        <div className="flex gap-x-2 items-center">
          <div
            className={`w-3 h-3 rounded-full mr-2 ${isServiceActive ? "bg-green-500" : "bg-gray-400"
              }`}
          ></div>
          <span
            className={`text-sm font-medium ${isServiceActive
                ? "text-green-700 dark:text-green-300"
                : "text-gray-700 dark:text-gray-300"
              }`}
          >
            {isServiceActive
              ? t("servicePageActivation.googleSheet.statusActive")
              : t("servicePageActivation.googleSheet.statusInactive")}
          </span>
        </div>
      </div>

      {/* Activation Form */}
      <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#9e68ba] mb-4">
              {isServiceActive
                ? t("servicePageActivation.googleSheet.formTitleActive")
                : t("servicePageActivation.googleSheet.formTitleInactive")}
            </h3>

            {/* App Script URL Input */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("servicePageActivation.googleSheet.inputLabel")}{" "}
                {!isServiceActive && <span className="text-red-500">*</span>}
              </label>
              <div className="flex">
                <input
                  type="text"
                  placeholder={t(
                    "servicePageActivation.googleSheet.inputPlaceholder"
                  )}
                  value={data.googleShit || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setData({ ...data, googleShit: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:!border-[#3c3a3d] rounded-lg rounded-r-none focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 bg-white dark:!bg-[#222222] text-gray-800 dark:text-gray-200"
                  style={{ direction: "ltr", textAlign: "left" }}
                />
                <button
                  type="button"
                  onClick={clearGoogleSheet}
                  className="px-3 py-2 bg-gray-200 dark:!bg-[#3c3a3d] hover:bg-gray-300 dark:hover:bg-[#4a4a4a] text-gray-700 dark:text-gray-200 rounded-lg rounded-l-none border border-gray-300 dark:!border-[#3c3a3d] border-l-0"
                >
                  <X size={18} />
                </button>
              </div>
              {data.googleShit?.length > 0 &&
                !(
                  data.googleShit.startsWith(
                    "https://script.google.com/macros/"
                  ) && data.googleShit.endsWith("/exec")
                ) && (
                  <p className="text-red-500 text-sm mt-1">
                    {t("servicePageActivation.googleSheet.inputValidation")}
                  </p>
                )}
              {!isServiceActive && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t("servicePageActivation.googleSheet.inputNote")}
                </p>
              )}
            </div>

            {/* Language Selection */}
            {(isServiceActive || data.googleShit) && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t("servicePageActivation.googleSheet.languageLabel")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-4">
                  <div
                    className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${data.frenchs
                        ? "bg-[#faf1ff] dark:!bg-[#3c3a3d] border-purple-300"
                        : "bg-white dark:!bg-[#222222] border-gray-300 dark:!border-[#3c3a3d] hover:bg-gray-50 dark:hover:bg-[#2a2a2a]"
                      }`}
                    onClick={handleFrench}
                  >
                    <input
                      type="checkbox"
                      checked={data.frenchs}
                      onChange={handleFrench}
                      className="h-4 w-4 text-[#9e68ba] rounded border-gray-300 focus:ring-purple-200 ml-2"
                    />
                    <span className="text-gray-700 dark:text-gray-200">
                      {t("servicePageActivation.googleSheet.languageFrench")}
                    </span>
                  </div>

                  <div
                    className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${data.arabics
                        ? "bg-[#faf1ff] dark:!bg-[#3c3a3d] border-purple-300"
                        : "bg-white dark:!bg-[#222222] border-gray-300 dark:!border-[#3c3a3d] hover:bg-gray-50 dark:hover:bg-[#2a2a2a]"
                      }`}
                    onClick={handleArabic}
                  >
                    <input
                      type="checkbox"
                      checked={data.arabics}
                      onChange={handleArabic}
                      className="h-4 w-4 text-[#9e68ba] rounded border-gray-300 focus:ring-purple-200 ml-2"
                    />
                    <span className="text-gray-700 dark:text-gray-200">
                      {t("servicePageActivation.googleSheet.languageArabic")}
                    </span>
                  </div>

                  <div
                    className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${data.numberS
                        ? "bg-[#faf1ff] dark:!bg-[#3c3a3d] border-purple-300"
                        : "bg-white dark:!bg-[#222222] border-gray-300 dark:!border-[#3c3a3d] hover:bg-gray-50 dark:hover:bg-[#2a2a2a]"
                      }`}
                    onClick={handleNumber}
                  >
                    <input
                      type="checkbox"
                      checked={data.numberS}
                      onChange={handleNumber}
                      className="h-4 w-4 text-[#9e68ba] rounded border-gray-300 focus:ring-purple-200 ml-2"
                    />
                    <span className="text-gray-700 dark:text-gray-200">
                      {t("servicePageActivation.googleSheet.languageNumbers")}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tips Section */}
            <div className="bg-green-50 dark:!bg-green-900/30 p-4 rounded-lg border border-green-100 dark:!border-green-700 mb-6">
              <h4 className="font-medium text-lg text-green-800 dark:text-green-300 mb-2 flex items-center">
                <Info size={18} className="ml-2" />
                {t("servicePageActivation.googleSheet.tipsTitle")}
              </h4>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 mr-5 list-disc">
                <li className="flex items-start">
                  <FileSpreadsheet
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {t("servicePageActivation.googleSheet.tips.customTable")}
                  </span>
                </li>
                <li className="flex items-start">
                  <ShieldAlert
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {t("servicePageActivation.googleSheet.tips.permissions")}
                  </span>
                </li>
                <li className="flex items-start">
                  <Database
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {t("servicePageActivation.googleSheet.tips.formulas")}
                  </span>
                </li>
                <li className="flex items-start">
                  <Settings
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {t("servicePageActivation.googleSheet.tips.formatting")}
                  </span>
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2.5 ${isServiceActive
                  ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                  : data.googleShit
                    ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                    : "bg-red-500 hover:bg-red-600"
                } text-white font-medium rounded-lg transition-colors flex items-center ${loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin ml-2" size={18} />
                  {data.googleShit
                    ? t("servicePageActivation.googleSheet.loadingSaving")
                    : t("servicePageActivation.googleSheet.loadingUnsaving")}
                </>
              ) : (
                <>
                  <Check className="ml-2" size={18} />
                  {data.googleShit
                    ? t("servicePageActivation.googleSheet.submitSaving")
                    : t("servicePageActivation.googleSheet.submitUnsaving")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>

  );
}
